import React, { useState } from 'react';
import { TextbookProgress } from '../types';
import { ChapterIndexItem, topics, chaptersIndex } from '../data/chaptersIndex';
import { getChapterAccess } from '../catalog/chapters';
import { useEntitlements } from '../hooks/useEntitlements';
import { TrendingUp, Flag, BookText, Landmark, Lightbulb, Heart, Gavel, Users, BookOpen } from 'lucide-react';

interface TopicChapterListProps {
  progress: TextbookProgress;
  onChapterSelect: (chapterId: string) => void;
  onNavigate?: (section: string) => void;
}

const TopicChapterList: React.FC<TopicChapterListProps> = ({
  progress,
  onChapterSelect,
  onNavigate,
}) => {
  const { hasPlus } = useEntitlements();
  const [expandedTopics, setExpandedTopics] = useState<Set<string>>(new Set());

  // Group chapters by topic
  const chaptersByTopic = chaptersIndex.reduce((acc, chapter) => {
    if (!acc[chapter.topic]) {
      acc[chapter.topic] = [];
    }
    acc[chapter.topic].push(chapter);
    return acc;
  }, {} as Record<string, ChapterIndexItem[]>);

  // Toggle topic expansion
  const toggleTopic = (topicId: string) => {
    const newExpanded = new Set(expandedTopics);
    if (newExpanded.has(topicId)) {
      newExpanded.delete(topicId);
    } else {
      newExpanded.add(topicId);
    }
    setExpandedTopics(newExpanded);
  };

  // Handle keyboard navigation for topics
  const handleTopicKeyDown = (event: React.KeyboardEvent, topicId: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleTopic(topicId);
    }
  };

  // Handle keyboard navigation for chapters
  const handleChapterKeyDown = (event: React.KeyboardEvent, chapterId: string, isLocked: boolean) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (isLocked) {
        onNavigate?.('upgrade');
      } else {
        onChapterSelect(chapterId);
      }
    }
  };

  // Calculate topic progress
  const getTopicProgress = (topicId: string) => {
    const topicChapters = chaptersByTopic[topicId] || [];
    const completedChapters = topicChapters.filter(chapter =>
      progress[chapter.id]?.isCompleted
    ).length;
    return {
      completed: completedChapters,
      total: topicChapters.length,
      percentage: topicChapters.length > 0 ? Math.round((completedChapters / topicChapters.length) * 100) : 0
    };
  };

  // Map topic IDs to lucide-react icons (same as flashcards)
  const getTopicIcon = (topicId: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      'ekonomie': <TrendingUp className="w-5 h-5" />,
      'evropska-integrace': <Flag className="w-5 h-5" />,
      'moderni-historie': <BookText className="w-5 h-5" />,
      'politologie': <Landmark className="w-5 h-5" />,
      'filosofie': <Lightbulb className="w-5 h-5" />,
      'psychologie': <Heart className="w-5 h-5" />,
      'pravo': <Gavel className="w-5 h-5" />,
      'sociologie': <Users className="w-5 h-5" />
    };
    return iconMap[topicId] || <BookOpen className="w-5 h-5" />;
  };

  // Get icon color based on topic (same as flashcards)
  const getIconColor = (topicId: string) => {
    const colorMap: { [key: string]: string } = {
      'ekonomie': 'text-green-600 dark:text-green-400',
      'evropska-integrace': 'text-blue-600 dark:text-blue-400',
      'moderni-historie': 'text-amber-600 dark:text-amber-400',
      'politologie': 'text-purple-600 dark:text-purple-400',
      'filosofie': 'text-indigo-600 dark:text-indigo-400',
      'psychologie': 'text-pink-600 dark:text-pink-400',
      'pravo': 'text-orange-600 dark:text-orange-400',
      'sociologie': 'text-teal-600 dark:text-teal-400'
    };
    return colorMap[topicId] || 'text-gray-600 dark:text-gray-400';
  };

  // Get background color for icon circle (same as flashcards)
  const getIconBgColor = (topicId: string) => {
    const bgMap: { [key: string]: string } = {
      'ekonomie': 'bg-green-100 dark:bg-green-900',
      'evropska-integrace': 'bg-blue-100 dark:bg-blue-900',
      'moderni-historie': 'bg-amber-100 dark:bg-amber-900',
      'politologie': 'bg-purple-100 dark:bg-purple-900',
      'filosofie': 'bg-indigo-100 dark:bg-indigo-900',
      'psychologie': 'bg-pink-100 dark:bg-pink-900',
      'pravo': 'bg-orange-100 dark:bg-orange-900',
      'sociologie': 'bg-teal-100 dark:bg-teal-900'
    };
    return bgMap[topicId] || 'bg-gray-100 dark:bg-gray-700';
  };

  // Get progress bar color based on topic
  const getProgressBarColor = (topicId: string) => {
    const colorMap: { [key: string]: string } = {
      'ekonomie': 'bg-green-500',
      'evropska-integrace': 'bg-blue-500',
      'moderni-historie': 'bg-amber-500',
      'politologie': 'bg-purple-500',
      'filosofie': 'bg-indigo-500',
      'psychologie': 'bg-pink-500',
      'pravo': 'bg-orange-500',
      'sociologie': 'bg-teal-500'
    };
    return colorMap[topicId] || 'bg-gray-500';
  };

  return (
    <section className="p-4 sm:p-6 pb-8 w-full overflow-x-hidden" aria-labelledby="topic-chapter-list-heading">
      <div className="mb-6">
        <h2 id="topic-chapter-list-heading" className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Dostupné kapitoly
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          Vyberte téma a poté kapitolu pro studium
        </p>
      </div>

      <div className="space-y-4" role="list">
        {topics.map((topic) => {
          const topicChapters = chaptersByTopic[topic.id] || [];
          const isExpanded = expandedTopics.has(topic.id);
          const topicProgress = getTopicProgress(topic.id);

          if (topicChapters.length === 0) return null;

          return (
            <div
              key={topic.id}
              className="bg-white dark:bg-dark-card rounded-lg border border-border shadow-[0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-md overflow-hidden"
              role="listitem"
            >
              {/* Topic Header */}
              <div
                tabIndex={0}
                onClick={() => toggleTopic(topic.id)}
                onKeyDown={(e) => handleTopicKeyDown(e, topic.id)}
                className="p-4 sm:p-5 cursor-pointer hover:bg-accent transition-colors duration-200 focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:outline-none touch-manipulation"
                aria-expanded={isExpanded}
                aria-controls={`topic-${topic.id}-chapters`}
                role="button"
              >
                {/* Mobile Layout */}
                <div className="flex sm:hidden items-center justify-between">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    {/* Icon circle instead of emoji */}
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0
                      ${getIconBgColor(topic.id)}
                    `}>
                      <div className={getIconColor(topic.id)}>
                        {getTopicIcon(topic.id)}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">
                        {topic.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          {topicProgress.completed}/{topicProgress.total}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {topicProgress.percentage}% dokončeno
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 flex-shrink-0">
                    {/* Progress bar */}
                    <div className="w-12 h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${getProgressBarColor(topic.id)} transition-all duration-300`}
                        style={{ width: `${topicProgress.percentage}%` }}
                      />
                    </div>

                    {/* Expand/collapse icon */}
                    <div
                      className={`transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''
                        }`}
                      aria-hidden="true"
                    >
                      <svg
                        className="w-5 h-5 text-gray-500 dark:text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden sm:flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {/* Icon circle instead of emoji */}
                    <div className={`
                      w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0
                      ${getIconBgColor(topic.id)}
                    `}>
                      <div className={getIconColor(topic.id)}>
                        {getTopicIcon(topic.id)}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {topic.title}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        {topic.description}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    {/* Progress indicator */}
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {topicProgress.completed}/{topicProgress.total}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {topicProgress.percentage}% dokončeno
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="w-16 h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${getProgressBarColor(topic.id)} transition-all duration-300`}
                        style={{ width: `${topicProgress.percentage}%` }}
                      />
                    </div>

                    {/* Expand/collapse icon */}
                    <div
                      className={`transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''
                        }`}
                      aria-hidden="true"
                    >
                      <svg
                        className="w-5 h-5 text-gray-500 dark:text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              {/* Chapters List */}
              <div
                id={`topic-${topic.id}-chapters`}
                className={`transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-[80vh] opacity-100' : 'max-h-0 opacity-0'
                  } overflow-hidden`}
              >
                <div className="border-t border-border bg-background relative">
                  {/* Scroll indicator for mobile */}
                  {isExpanded && topicChapters.length > 6 && (
                    <div className="absolute top-2 right-2 z-10 bg-blue-500 text-white text-xs px-2 py-1 rounded-full opacity-75 sm:hidden">
                      Posuňte pro více
                    </div>
                  )}
                  <div className="max-h-[70vh] overflow-y-auto overflow-x-hidden mobile-scroll-container scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
                    <div className="flex flex-col gap-2 p-3">
                      {topicChapters.map((chapter) => {
                        const chapterProgress = progress[chapter.id];
                        const isCompleted = chapterProgress?.isCompleted || false;
                        const isLocked = getChapterAccess(chapter.id) === 'pro' && !hasPlus;

                        return (
                          <div
                            key={chapter.id}
                            tabIndex={0}
                            onClick={() => {
                              if (isLocked) {
                                onNavigate?.('upgrade');
                              } else {
                                onChapterSelect(chapter.id);
                              }
                            }}
                            onKeyDown={(e) => handleChapterKeyDown(e, chapter.id, isLocked)}
                            className="group flex items-center gap-3 px-3 py-3 rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:bg-gray-100 dark:hover:bg-white/[0.07] hover:border-gray-300 dark:hover:border-white/[0.12] focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-150 cursor-pointer touch-manipulation w-full min-w-0"
                            aria-label={`${chapter.title} - ${isCompleted ? 'Dokončeno' : 'Nedokončeno'}${isLocked ? ' - Vyžaduje PREMIUM' : ''}`}
                          >
                            {/* Bullet / completion indicator */}
                            {!isLocked && isCompleted ? (
                              <div
                                className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0"
                                title="Kapitola dokončena"
                              >
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            ) : (
                              <div className="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-500 flex-shrink-0" />
                            )}

                            {/* Chapter title */}
                            <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate flex-1">
                              {chapter.title}
                            </span>

                            {/* Right side: badges + arrow */}
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {isLocked && (
                                <div className="bg-yellow-400 text-yellow-950 text-xs font-black tracking-wide px-2 py-0.5 rounded-md flex items-center">
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                  </svg>
                                  PREMIUM
                                </div>
                              )}
                              {!isLocked && isCompleted && chapterProgress?.quizScore !== undefined && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {chapterProgress.quizScore}%
                                </span>
                              )}
                              <svg className="w-4 h-4 text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity duration-150 -mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {topics.every(topic => (chaptersByTopic[topic.id] || []).length === 0) && (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Žádné kapitoly nejsou k dispozici
          </h3>
          <p className="text-gray-600 dark:text-gray-300">
            Kapitoly budou přidány brzy.
          </p>
        </div>
      )}
    </section>
  );
};

export default TopicChapterList;